import React from 'react';
import { omit } from 'lodash';
import { styled } from '@mui/material';
import { Draggable } from 'react-beautiful-dnd';

import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { TaskCard } from 'shell/components';
import { TYPES } from 'utils/constants';
import { getUserPreferredFullName } from 'utils/helper';

const KanbanTaskCard = ({
    index,
    task,
    isSorted,
    onOpenTask,
    onDeleteTask,
    onDuplicateTask,
    updateTask,
    onMouseEnter,
    onMouseLeave,
}) => {
    const { taskList, subtaskList, updateSubtask } = useTask();
    const { accountUsers, currentGroupAccountUsers } = useUser2();

    task.subtasks =
        task.type === TYPES.MILESTONE
            ? Object.values(taskList.all).filter(
                  ({ active, type, milestone_id }) =>
                      active && type !== TYPES.MILESTONE && milestone_id === task.milestone_id
              )
            : Object.values(subtaskList).filter(({ active, parent_id }) => active && parent_id === task.id);

    const actionOptions = [
        { key: 'edit', value: 'Edit' },
        { key: 'delete', value: 'Delete', disabled: [TYPES.TEMPLATE, TYPES.MILESTONE].includes(task.type) },
        { key: 'duplicate', value: 'Duplicate', disabled: [TYPES.TEMPLATE, TYPES.MILESTONE].includes(task.type) },
    ];

    const handleClickComplete = (taskId, newStatus) => {
        if (Object.keys(taskList.all).includes(taskId)) {
            updateTask(taskId, 'status', newStatus, true);
        }

        if (Object.keys(subtaskList).includes(taskId)) {
            updateSubtask(taskId, 'status', newStatus);
        }
    };

    const handleSelectAction = ({ key: action }) => {
        switch (action) {
            case 'edit': {
                return onOpenTask(task.id);
            }
            case 'delete': {
                return onDeleteTask(task.id);
            }
            case 'duplicate': {
                return onDuplicateTask(task.id);
            }
            default:
                break;
        }
    };

    const handleChangeCollaborators = (taskId, accountUser) => {
        if (Object.keys(taskList.all).includes(taskId)) {
            const { collaborator_account_ids } = taskList.all[taskId];
            const newCollaborators = collaborator_account_ids?.includes(accountUser.owner_account_id)
                ? collaborator_account_ids.filter((id) => id !== accountUser.owner_account_id)
                : [...(collaborator_account_ids || []), accountUser.owner_account_id];
            updateTask(taskId, 'collaborator_account_ids', newCollaborators, true);
        }

        if (Object.keys(subtaskList).includes(taskId)) {
            const { collaborator_account_ids } = subtaskList[taskId];
            const newCollaborators = collaborator_account_ids?.includes(accountUser.owner_account_id)
                ? collaborator_account_ids.filter((id) => id !== accountUser.owner_account_id)
                : [...(collaborator_account_ids || []), accountUser.owner_account_id];
            updateSubtask(taskId, 'collaborator_account_ids', newCollaborators);
        }
    };

    const handleKeyDown = (callback) => (e) => {
        const { currentTarget, key, target } = e;
        if (target === currentTarget) {
            if (key === 'Enter') {
                onOpenTask(task.id);
            } else {
                callback(e);
            }
        }
    };

    return (
        <Draggable draggableId={task.id} index={index}>
            {(provided, snapshot) => (
                <CustomTaskCard
                    data-cy={task.title}
                    ref={provided.innerRef}
                    id={task.id}
                    isSorted={isSorted}
                    show={task.show}
                    snapshot={snapshot}
                    task={task}
                    variant={task.type === TYPES.MILESTONE ? TYPES.MILESTONE : 'default'}
                    getNameById={(id) =>
                        getUserPreferredFullName(accountUsers.find(({ owner_account_id }) => owner_account_id === id))
                    }
                    getUserById={(id) => accountUsers.find(({ owner_account_id }) => owner_account_id === id)}
                    actionOptions={actionOptions}
                    collaboratorOptions={currentGroupAccountUsers}
                    onChangeCollaborator={(taskId, accountUser) => handleChangeCollaborators(taskId, accountUser)}
                    onClick={onOpenTask}
                    onClickComplete={handleClickComplete}
                    onSelectAction={handleSelectAction}
                    onKeyDown={handleKeyDown(provided.dragHandleProps.onKeyDown)}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    {...provided.draggableProps}
                    {...omit(provided.dragHandleProps, ['onKeyDown'])}
                    aria-roledescription={undefined}
                />
            )}
        </Draggable>
    );
};

const CustomTaskCard = styled(TaskCard)(({ isSorted, show, snapshot, theme }) => {
    return {
        marginBottom: theme.spacing(5),
        '& > div': {
            cursor: 'grab',
        },
        ...(isSorted && {
            ...(!snapshot.isDragging && {
                transform: 'none !important',
            }),
            ...(snapshot.isDropAnimating && {
                transitionDuration: '0.001s !important',
            }),
        }),
        ...(!show && {
            display: 'none',
        }),
        ...(snapshot.isDragging && {
            boxShadow: theme.palette.ensightful.shadows[2],
        }),
    };
});

export default KanbanTaskCard;
